/**
 * Shared UI primitives + hooks used across every authenticated Femcare-web
 * page. Kept in one small module so pages import a single, stable API.
 */
import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/* ---------- Layout ---------- */
export function Page({ eyebrow, title, subtitle, actions, children }) {
    return (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8"
             data-testid={`app-page-${(title||"").toLowerCase().replace(/\s+/g,"-")}`}>
            <header className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    {eyebrow && <p className="text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-bold text-aurora-magenta">{eyebrow}</p>}
                    <h1 className="mt-1 text-[34px] md:text-[44px] leading-[1.05] text-[#1C1424] break-words fp-serif">{title}</h1>
                    {subtitle && <p className="mt-1 text-sm text-aurora-ink/65 max-w-2xl">{subtitle}</p>}
                </div>
                {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
            </header>
            {children}
        </div>
    );
}

const PANEL_TONES = {
    pearl: "bg-white border-[#EEE7EC]",
    plum:  "bg-aurora-plum text-aurora-pearl border-aurora-plum",
    lav:   "bg-aurora-lavender/15 border-aurora-lavender/30",
    peach: "bg-aurora-peach/25 border-aurora-peach/40",
    rose:  "bg-aurora-rose/15 border-aurora-rose/30",
    white: "bg-white border-aurora-lavender/25",
};
export function Panel({ tone="pearl", className="", children, ...rest }) {
    return <section {...rest}
        className={`rounded-[24px] border p-5 md:p-6 shadow-[0_30px_60px_-40px_rgba(36,17,48,0.38)] ${PANEL_TONES[tone]} ${className}`}>
        {children}
    </section>;
}

export function Chip({ children, active=false, onClick, testid }) {
    return <button type="button" onClick={onClick} data-testid={testid}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition ${
            active
                ? "bg-aurora-magenta text-white shadow-sm"
                : "bg-aurora-lavender/25 text-aurora-plum hover:bg-aurora-lavender/40"
        }`}>{children}</button>;
}

export function CTA({ children, onClick, disabled, testid, variant="primary", type="button" }) {
    const cls = variant === "primary"
        ? "text-white shadow-lg"
        : variant === "ghost"
            ? "text-aurora-plum bg-aurora-lavender/20 hover:bg-aurora-lavender/40"
            : "text-aurora-plum border border-aurora-plum/30 bg-white hover:bg-aurora-lavender/10";
    const style = variant === "primary"
        ? { background: "linear-gradient(135deg,#2A1233 0%,#6A2A62 60%,#D62E6E 100%)" }
        : undefined;
    return <button type={type} data-testid={testid} onClick={onClick} disabled={disabled}
        className={`inline-flex items-center gap-2 rounded-full px-4 md:px-5 py-2.5 text-sm font-bold disabled:opacity-60 transition ${cls}`}
        style={style}>{children}</button>;
}

/* ---------- State fetching ---------- */
export function useApi(fn, deps=[]) {
    const [s, set] = useState({ data:null, loading:true, error:null });
    useEffect(() => { let dead=false; set({data:null,loading:true,error:null});
        Promise.resolve().then(fn).then(d=>!dead&&set({data:d,loading:false,error:null}))
            .catch(e=>!dead&&set({data:null,loading:false,error:e.message||String(e)}));
        return ()=>{dead=true};
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return s;
}

export function StateBlock({ loading, error, data, empty="No data yet.", emptyTestid, children }) {
    if (loading) return <div className="text-sm text-aurora-ink/50 py-4 animate-pulse" data-testid="loading">Loading…</div>;
    if (error)   return <div className="text-sm text-aurora-magenta py-3" data-testid="error-msg">{error}</div>;
    if (!data || (Array.isArray(data) && data.length === 0))
        return <div className="text-sm text-aurora-ink/50 py-3" data-testid={emptyTestid || "empty"}>{empty}</div>;
    return children;
}

/* ---------- Form primitives ---------- */
export function Field({ label, hint, children, error }) {
    return <label className="block mb-3">
        <span className="text-[11px] font-bold text-aurora-plum/80 uppercase tracking-[0.14em]">{label}</span>
        {children}
        {hint && !error && <p className="text-[11px] text-aurora-ink/50 mt-1">{hint}</p>}
        {error && <p className="text-[11px] text-aurora-magenta mt-1">{error}</p>}
    </label>;
}

export function Input({ testid, ...props }) {
    return <input {...props} data-testid={testid}
        className={`mt-2 w-full rounded-xl border border-aurora-lavender/40 bg-white px-3 md:px-4 py-2.5 text-aurora-plum placeholder:text-aurora-ink/40 focus:ring-2 focus:ring-aurora-magenta/40 focus:border-aurora-magenta focus:outline-none disabled:bg-aurora-lavender/10 ${props.className||""}`} />;
}

export function Textarea({ testid, ...props }) {
    return <textarea {...props} data-testid={testid}
        className="mt-2 w-full rounded-xl border border-aurora-lavender/40 bg-white px-3 md:px-4 py-2.5 text-aurora-plum placeholder:text-aurora-ink/40 focus:ring-2 focus:ring-aurora-magenta/40 focus:border-aurora-magenta focus:outline-none" />;
}

export function Select({ testid, options, value, onChange, placeholder, ...props }) {
    return <select value={value ?? ""} onChange={onChange} data-testid={testid} {...props}
        className="mt-2 w-full rounded-xl border border-aurora-lavender/40 bg-white px-3 md:px-4 py-2.5 text-aurora-plum focus:ring-2 focus:ring-aurora-magenta/40 focus:border-aurora-magenta focus:outline-none">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>;
}

/**
 * Multi-select chip grid — responsive: 2 cols mobile, 3-4 tablet, 6 desktop.
 * Same option set is shown at all widths — never hides options.
 */
export function ChipGrid({ options, values=[], onToggle, testidPrefix="chip", cols="default" }) {
    const colCls = cols === "wide"
        ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
        : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4";
    return <div className={`grid ${colCls} gap-2 mt-2`} role="group">
        {options.map(o => {
            const active = values.includes(o.value);
            return <button key={o.value} type="button" onClick={()=>onToggle(o.value)}
                data-testid={`${testidPrefix}-${o.value}`}
                className={`px-3 py-2 rounded-xl text-xs md:text-sm font-semibold border transition text-center ${
                    active
                        ? "bg-aurora-magenta text-white border-aurora-magenta shadow"
                        : "bg-white text-aurora-plum border-aurora-lavender/40 hover:border-aurora-magenta hover:bg-aurora-lavender/10"
                }`}>
                {o.emoji ? <span className="mr-1">{o.emoji}</span> : null}{o.label}
            </button>;
        })}
    </div>;
}

/**
 * Single-select segmented pill row (e.g. Flow level).
 */
export function Segmented({ options, value, onChange, testidPrefix="seg" }) {
    return <div className="flex flex-wrap gap-2 mt-2">
        {options.map(o => {
            const active = value === o.value;
            return <button key={o.value} type="button" onClick={()=>onChange(active ? null : o.value)}
                data-testid={`${testidPrefix}-${o.value}`}
                className={`px-3 py-2 rounded-full text-xs md:text-sm font-semibold border transition ${
                    active
                        ? "bg-aurora-magenta text-white border-aurora-magenta shadow"
                        : "bg-white text-aurora-plum border-aurora-lavender/40 hover:border-aurora-magenta"
                }`}>{o.label}</button>;
        })}
    </div>;
}

/* ---------- Modal ---------- */
export function Modal({ open, onClose, title, children, testid="modal", footer }) {
    const ref = useRef(null);
    useEffect(() => { if (open && ref.current) ref.current.focus(); }, [open]);
    if (!open) return null;
    return <div role="dialog" aria-modal="true" data-testid={testid}
        className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-aurora-ink/40 backdrop-blur-sm p-2 md:p-6">
        <div ref={ref} tabIndex={-1}
            className="w-full md:max-w-2xl max-h-[92vh] overflow-auto bg-white rounded-t-3xl md:rounded-3xl shadow-2xl border border-aurora-lavender/40">
            <div className="sticky top-0 flex items-center justify-between bg-white/95 backdrop-blur border-b border-aurora-lavender/25 px-5 py-3">
                <h3 className="font-extrabold text-aurora-plum">{title}</h3>
                <button onClick={onClose} data-testid={`${testid}-close`} className="text-aurora-plum/70 hover:text-aurora-magenta">
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className="p-5">{children}</div>
            {footer && <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-aurora-lavender/25 px-5 py-3 flex justify-end gap-2">{footer}</div>}
        </div>
    </div>;
}

/* ---------- Toast ---------- */
export function useToast() {
    const [msg, setMsg] = useState(null);
    const show = (text, kind="info") => {
        setMsg({ text, kind });
        setTimeout(() => setMsg(null), 3500);
    };
    const node = msg
        ? <div role="status" data-testid="toast"
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-full px-5 py-2.5 text-sm font-semibold shadow-xl ${
                msg.kind==="error" ? "bg-aurora-magenta text-white" : "bg-aurora-plum text-white"
            }`}>{msg.text}</div>
        : null;
    return { show, node };
}

/* ---------- Helpers ---------- */
export const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

/* ============================================================================
 * DataList — renders any FT response (object OR array of primitives) as calm
 * Femcare-style key/value pairs. Replaces raw JSON <pre>. Non-recursive: nested
 * objects are pretty-printed on one line.
 * ============================================================================ */
function humanizeKey(k) {
    return String(k).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function stringifyInline(v, depth) {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (typeof v === "number") return String(v);
    if (typeof v === "string") {
        if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
            try { return new Date(v).toLocaleString(); } catch { return v; }
        }
        return v;
    }
    if (Array.isArray(v)) {
        if (v.length === 0) return "None";
        if (v.every((x) => typeof x !== "object" || x === null)) return v.join(", ");
        if (depth <= 0) return `${v.length} item${v.length === 1 ? "" : "s"}`;
        return v.map((x) => stringifyInline(x, depth - 1)).join(" · ");
    }
    if (typeof v === "object") {
        const parts = Object.entries(v)
            .filter(([k]) => !k.startsWith("_"))
            .slice(0, 6)
            .map(([k, val]) => `${humanizeKey(k)}: ${stringifyInline(val, depth - 1)}`);
        return parts.join(" · ") || "—";
    }
    return String(v);
}

/**
 * Render a plain-JSON payload as human-readable rows.
 *   <DataList data={obj} emptyLabel="Nothing here yet."/>
 */
export function DataList(props) {
    const data = props.data;
    const emptyLabel = props.emptyLabel || "No data yet.";
    const className = props.className || "";
    const testid = props["data-testid"];

    if (data === null || data === undefined) {
        return <p className="text-sm text-aurora-ink/55" data-testid={testid}>{emptyLabel}</p>;
    }
    if (typeof data !== "object") {
        return <p className="text-sm text-aurora-plum/85" data-testid={testid}>{String(data)}</p>;
    }
    let entries;
    if (Array.isArray(data)) {
        if (data.length === 0) {
            return <p className="text-sm text-aurora-ink/55" data-testid={testid}>{emptyLabel}</p>;
        }
        entries = data.map((row, i) => [`Item ${i + 1}`, row]);
    } else {
        entries = Object.entries(data).filter(([k]) => !k.startsWith("_"));
        if (entries.length === 0) {
            return <p className="text-sm text-aurora-ink/55" data-testid={testid}>{emptyLabel}</p>;
        }
    }
    return (
        <dl className={"divide-y divide-aurora-lavender/15 " + className} data-testid={testid}>
            {entries.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[7rem_1fr] md:grid-cols-[10rem_1fr] gap-3 py-2 items-start">
                    <dt className="text-[11px] uppercase tracking-widest text-aurora-plum/60 font-bold pt-0.5">
                        {humanizeKey(k)}
                    </dt>
                    <dd className="text-sm text-aurora-plum/90 break-words whitespace-pre-wrap">
                        {stringifyInline(v, 2)}
                    </dd>
                </div>
            ))}
        </dl>
    );
}
